import {inject, injectable} from 'inversify';

import {courseStore} from '../../../../../api/course';
import {
  type EditionApiModel,
  editionStore,
  type ProgramApiModel,
  programStore,
} from '../../../../../api/program';
import {currentUserStore} from '../../../../../api/user';
import {isCourseIndividual} from '../../../../../utils/entity/course';
import {AppError} from '../../../../../utils/errors/app';
import {CourseMapperJrpc} from '../../../../domain/Course/mappers/Course.mapper.jrpc';
import {CreateCourseCmd} from '../CreateCourse.cmd';

type ProgramWithEditions = ProgramApiModel & {
  editions: EditionApiModel[];
};

@injectable()
export class CreateCourseCmdJrpc implements CreateCourseCmd {
  constructor(
    @inject(CourseMapperJrpc)
    private readonly mapper: CourseMapperJrpc,
  ) {}

  async exec({
    label,
    programId,
    startDate,
  }: CreateCourseCmd.Props): Promise<CreateCourseCmd.Response> {
    const expand = this.mapper.expand.getByEnv();
    const program = (await programStore.coach.fetch({
      _id: programId,
      expand: {
        program: ['edition_ids'],
      },
    })) as ProgramWithEditions;

    const lastEdition = program.editions.at(-1);

    if (!lastEdition) {
      throw new AppError('Program edition not found');
    }

    if (lastEdition.status === 'draft') {
      await editionStore.sign(lastEdition, {
        sign: currentUserStore.user!.name,
      });

      lastEdition.status = 'active';

      await programStore.localUpdate(program, {});
    }

    const course = (await courseStore.create({
      edition_id: lastEdition._id,
      expand,
      label,
      start_date: startDate.toISOString(),
    })) as CourseMapperJrpc.ExpandedApiData;

    if (!isCourseIndividual(course)) {
      const editionToUpdate = course.edition
        ? course.edition
        : await editionStore.fetch({
            _id: lastEdition._id,
          });

      const newEdition = await editionStore.update(editionToUpdate, {
        course_ids: [...editionToUpdate.course_ids, course._id],
        revision: editionToUpdate.revision,
      });

      program.editions[program.editions.length - 1] = newEdition;

      programStore.localUpdate(program, {});
    }

    return this.mapper.mapDataToModel(course);
  }
}
